import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import useStyles from './ContentContainer.styles';
import clsx from 'clsx';
import RawHtml from '../../RawHtml/RawHtml';
import Quotation from '../Quotation/Quotation';
import LinkContainer from '../LinkContainer/LinkContainer';
import { getTextContentPositionClass } from '../../../utils/common';
import Link from '../../Link/Link';
const DESKTOP = 'DESKTOP';
const MOBILE_NEW = 'MOBILE_NEW';
const MOBILE_OLD = 'MOBILE_OLD';
const MOBILE_VIDEO = 'MOBILE_VIDEO';
export const getTextAlignmentClass = (textAlignment, classes) => {
    switch (textAlignment === null || textAlignment === void 0 ? void 0 : textAlignment.toUpperCase()) {
        case 'RIGHT':
            return classes.textRight;
        case 'LEFT':
            return classes.textLeft;
        case 'CENTER':
            return classes.textCenter;
        default:
            return classes.textCenter;
    }
};
// Helper function to render message content
const renderMessageContent = (message, citation, classNames, classes, isQuotation, shouldShowMessage = true) => {
    const quotationClass = isQuotation ? classNames === null || classNames === void 0 ? void 0 : classNames.quotationMessage : '';
    return (_jsxs("div", { className: shouldShowMessage ? classes.showMessageContainer : classes.hideMessage, children: [message && (_jsx(RawHtml, { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.message, citation ? classes.citationMessage : quotationClass, isQuotation && citation && (classNames === null || classNames === void 0 ? void 0 : classNames.quotationMessage)), content: message })), citation && (_jsx(Box, { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.message, classes.citation, quotationClass), component: 'p', children: citation }))] }));
};
// Helper function to determine mobile view type
const getMobileViewType = (isMobile, hasVideo, renderNewStyle) => {
    if (!isMobile)
        return DESKTOP;
    if (hasVideo)
        return MOBILE_VIDEO;
    if (renderNewStyle)
        return MOBILE_NEW;
    return MOBILE_OLD;
};
// Helper function to get container classes
const getContainerClasses = (classNames, classes, textContentPositionClass, textAlignmentClass, options) => {
    const { isFlyingFlowers, isQuotation, isFromContentFeature, isMobile } = options;
    return clsx(classNames === null || classNames === void 0 ? void 0 : classNames.container, classes === null || classes === void 0 ? void 0 : classes.contentFeature, classNames === null || classNames === void 0 ? void 0 : classNames.content, !isFlyingFlowers ? textContentPositionClass : '', textAlignmentClass, isQuotation ? classes.contentWithQuotationMarks : '', isFromContentFeature && isMobile ? classes.mobileContentFeature : classes.desktopContentFeature);
};
const ContentContainer = (props) => {
    const { heading, message, citation, textAlignment, links, linkStyle, buttonVariant, isQuotation, isFlyingFlowers, textContentPosition, classNames, titleTextVariant, headingColor, maxLinks, headingTag, contentFeatureAnalytics, contentFeatureLinkContainer, isFromContentFeature, isShowMore: externalIsShowMore, setIsShowMore: externalSetIsShowMore, renderNewStyle, hasVideo, } = props;
    const isButton = (linkStyle === null || linkStyle === void 0 ? void 0 : linkStyle.toUpperCase()) === 'BUTTON';
    const { classes } = useStyles({ renderNewStyle });
    const [internalIsShowMore, setInternalIsShowMore] = useState(false);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const mobileViewType = getMobileViewType(isMobile, hasVideo, renderNewStyle);
    // Extract state management logic for better readability
    const shouldUseExternalState = useMemo(() => {
        return (renderNewStyle &&
            !hasVideo &&
            isMobile &&
            isFromContentFeature &&
            externalIsShowMore !== undefined &&
            externalSetIsShowMore);
    }, [renderNewStyle, hasVideo, isMobile, isFromContentFeature, externalIsShowMore, externalSetIsShowMore]);
    // Determine the current show more state
    let isShowMore;
    if (shouldUseExternalState) {
        isShowMore = externalIsShowMore;
    }
    else if (!renderNewStyle || hasVideo) {
        isShowMore = true;
    }
    else {
        isShowMore = internalIsShowMore;
    }
    const setIsShowMore = shouldUseExternalState ? externalSetIsShowMore : setInternalIsShowMore;
    const HeadingTag = headingTag ? headingTag === null || headingTag === void 0 ? void 0 : headingTag.toLowerCase() : 'div';
    // Helper function to determine if links should be shown
    const shouldShowLinks = useMemo(() => {
        return !isMobile || hasVideo || !renderNewStyle || (!renderNewStyle && !isShowMore);
    }, [isMobile, hasVideo, renderNewStyle, isShowMore]);
    const textContentPositionClass = getTextContentPositionClass(textContentPosition, classNames, 'CENTER');
    const textAlignmentClass = getTextAlignmentClass(textAlignment, classes);
    const containerClasses = getContainerClasses(classNames, classes, textContentPositionClass, textAlignmentClass, {
        isFlyingFlowers,
        isQuotation,
        isFromContentFeature,
        isMobile,
    });
    return (_jsx(Box, { className: containerClasses, children: (heading || message || citation || !!(links === null || links === void 0 ? void 0 : links.length)) && (_jsxs("div", { className: clsx(classes.contentInner, classNames === null || classNames === void 0 ? void 0 : classNames.contentInner, classNames === null || classNames === void 0 ? void 0 : classNames.mediaContentInner, isQuotation ? classes.contentFeatureQuoteContainer : '', isFromContentFeature && isMobile && renderNewStyle && !hasVideo ? classes.contentFeatureInnerContent : ''), children: [_jsx(Quotation, { isQuotation: isQuotation, heading: heading, headingColor: headingColor, titleTextVariant: titleTextVariant, classNames: classNames, headingTag: HeadingTag, setIsShowMore: setIsShowMore, isShowMore: isShowMore, renderNewStyle: renderNewStyle }), mobileViewType === 'DESKTOP' && renderMessageContent(message, citation, classNames, classes, isQuotation), mobileViewType === 'MOBILE_NEW' && (_jsx(_Fragment, { children: renderMessageContent(message, citation, classNames, classes, isQuotation, isShowMore) })), (mobileViewType === 'MOBILE_OLD' || mobileViewType === 'MOBILE_VIDEO') && (_jsx(_Fragment, { children: renderMessageContent(message, citation, classNames, classes, isQuotation) })), mobileViewType === 'MOBILE_NEW' && message && (_jsx(Link, { label: `Read ${isShowMore ? 'less' : 'more'}`, onClick: () => setIsShowMore === null || setIsShowMore === void 0 ? void 0 : setIsShowMore(!isShowMore), className: isShowMore ? classes.readLessLink : classes.readMoreLink, icon: "call_made", iconPosition: "right" })), links && shouldShowLinks && (_jsx(LinkContainer, { links: links, isButton: isButton, buttonVariant: buttonVariant, classNames: classNames, maxLinks: maxLinks, contentFeatureAnalytics: contentFeatureAnalytics, contentFeatureLinkContainer: contentFeatureLinkContainer }))] })) }));
};
export default ContentContainer;
